import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X, CalendarDays, Inbox, Mail, Users } from "lucide-react";
import { LOGO } from "@/data/content";

const CATEGORIES = ["Concerts", "Community", "Private Events", "Special Events"];
const STATUSES = ["new", "contacted", "confirmed", "closed"];
const emptyEvent = { title: "", category: "Concerts", date: "", time: "", description: "", image_url: "", ticket_url: "", featured: false };

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-epic-charcoal border border-white/5 p-6">
      <Icon className="text-epic-gold" size={22} />
      <p className="font-display text-4xl text-white mt-3">{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-white/50 mt-1">{label}</p>
    </div>
  );
}

function ContentField({ label, value, onChange, textarea, testid }) {
  const cls = "w-full bg-transparent border border-white/15 text-white px-3 py-2 text-sm focus:outline-none focus:border-epic-gold";
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-white/70 mb-2">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={cls} />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("events");
  const [stats, setStats] = useState({});
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subs, setSubs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/events", { params: { limit: 100 } }).then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Site content updated. Refresh the homepage to see changes.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingContent(false);
    }
  };

  const saveEvent = async () => {
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, editing);
      else await api.post("/admin/events", editing);
      toast.success("Event saved");
      setEditing(null);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Deleted"); loadAll();
  };
  const setStatus = async (id, status) => {
    await api.put(`/admin/bookings/${id}/status`, { status });
    toast.success("Status updated"); loadAll();
    setSelected((s) => (s && s.id === id ? { ...s, status } : s));
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/bookings/${id}`);
    setSelected(null); toast.success("Deleted"); loadAll();
  };

  if (loading || !user) return <div className="min-h-screen bg-epic-black flex items-center justify-center text-white/50">Loading…</div>;

  const inputCls = "w-full bg-transparent border border-white/15 text-white px-3 py-2 text-sm focus:outline-none focus:border-epic-gold";

  return (
    <div className="min-h-screen bg-epic-black text-white">
      <header className="border-b border-white/5 bg-epic-charcoal">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="The Epic Center logo" className="h-12 md:h-14 w-auto object-contain" />
            <span className="font-display text-2xl">THE EPIC CENTER</span>
            <span className="text-xs tracking-[0.3em] text-epic-gold uppercase ml-1">Admin</span>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 text-white/70 hover:text-epic-gold text-sm uppercase tracking-widest">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={CalendarDays} label="Events" value={stats.events ?? 0} testid="stat-events" />
          <StatCard icon={Inbox} label="Total Inquiries" value={stats.bookings ?? 0} testid="stat-bookings" />
          <StatCard icon={Users} label="New Inquiries" value={stats.new_bookings ?? 0} testid="stat-new-bookings" />
          <StatCard icon={Mail} label="Subscribers" value={stats.subscribers ?? 0} testid="stat-subscribers" />
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/10">
          {[["events", "Events"], ["bookings", "Inquiries"], ["newsletter", "Newsletter"], ["content", "Site Content"]].map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-3 text-sm uppercase tracking-widest border-b-2 -mb-px ${tab === k ? "border-epic-gold text-epic-gold" : "border-transparent text-white/50 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "events" && (
          <div>
            <button data-testid="admin-add-event" onClick={() => setEditing({ ...emptyEvent })}
              className="flex items-center gap-2 bg-epic-gold text-epic-black font-bold uppercase tracking-widest text-xs px-5 py-3 mb-6 hover:bg-epic-goldhover transition-colors">
              <Plus size={16} /> Add Event
            </button>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((ev) => (
                <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="bg-epic-charcoal border border-white/5 overflow-hidden">
                  <img src={ev.image_url || "https://images.unsplash.com/photo-1764255510960-deee566a91f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"} alt={ev.title} className="w-full h-40 object-cover" />
                  <div className="p-4">
                    <span className="text-[0.6rem] uppercase tracking-widest text-epic-gold">{ev.category}</span>
                    <h3 className="font-oswald text-lg uppercase mt-1">{ev.title}</h3>
                    <p className="text-white/50 text-xs mt-1">{ev.date} · {ev.time}</p>
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`admin-edit-event-${ev.id}`} onClick={() => setEditing(ev)} className="flex items-center gap-1 text-xs text-white/70 hover:text-epic-gold"><Pencil size={14} /> Edit</button>
                      <button data-testid={`admin-delete-event-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300"><Trash2 size={14} /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/40 uppercase text-xs tracking-widest border-b border-white/10">
                  <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Event</th><th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Tag</th><th className="py-3 pr-4">Status</th><th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`admin-booking-${b.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4">{b.full_name}<div className="text-white/40 text-xs">{b.email}</div></td>
                    <td className="py-3 pr-4">{b.event_type}</td>
                    <td className="py-3 pr-4">{b.event_date || "—"}</td>
                    <td className="py-3 pr-4"><span className="text-epic-gold text-xs">{b.lead_tag}</span></td>
                    <td className="py-3 pr-4">
                      <select data-testid={`admin-booking-status-${b.id}`} value={b.status} onChange={(e) => setStatus(b.id, e.target.value)}
                        className="bg-transparent border border-white/15 text-white text-xs px-2 py-1 focus:outline-none focus:border-epic-gold">
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-epic-charcoal">{s}</option>)}
                      </select>
                    </td>
                    <td className="py-3"><button data-testid={`admin-view-booking-${b.id}`} onClick={() => setSelected(b)} className="text-epic-gold text-xs uppercase tracking-widest hover:underline">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bookings.length === 0 && <p className="text-white/40 py-10 text-center">No inquiries yet.</p>}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="bg-epic-charcoal border border-white/5">
            {subs.map((s) => (
              <div key={s.id} className="flex justify-between px-5 py-3 border-b border-white/5 text-sm">
                <span>{s.email}</span>
                <span className="text-white/40 text-xs">{(s.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
            {subs.length === 0 && <p className="text-white/40 py-10 text-center">No subscribers yet.</p>}
          </div>
        )}

        {tab === "content" && content && (
          <div className="max-w-3xl">
            <p className="text-white/50 text-sm mb-6">Edit the text and images shown on your homepage. Click Save, then refresh the site to see the changes live.</p>
            <div className="space-y-6">
              <ContentField label="Hero Title" value={content.hero_title} onChange={(v) => setContent({ ...content, hero_title: v })} testid="content-hero-title" />
              <ContentField label="Hero Subtitle" value={content.hero_subtitle} onChange={(v) => setContent({ ...content, hero_subtitle: v })} testid="content-hero-subtitle" />
              <ContentField label="About Heading" value={content.about_heading} onChange={(v) => setContent({ ...content, about_heading: v })} testid="content-about-heading" />
              <ContentField label="About Paragraph 1" value={content.about_body1} onChange={(v) => setContent({ ...content, about_body1: v })} textarea testid="content-about-body1" />
              <ContentField label="About Paragraph 2" value={content.about_body2} onChange={(v) => setContent({ ...content, about_body2: v })} textarea testid="content-about-body2" />
              <div>
                <ContentField label="About Image URL" value={content.about_image} onChange={(v) => setContent({ ...content, about_image: v })} testid="content-about-image" />
                {content.about_image && <img src={content.about_image} alt="About preview" className="mt-3 h-32 w-auto object-cover border border-white/10" />}
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <ContentField label="Address Line 1" value={content.contact_address1} onChange={(v) => setContent({ ...content, contact_address1: v })} testid="content-address1" />
                <ContentField label="Address Line 2" value={content.contact_address2} onChange={(v) => setContent({ ...content, contact_address2: v })} testid="content-address2" />
                <ContentField label="Phone 1" value={content.contact_phone1} onChange={(v) => setContent({ ...content, contact_phone1: v })} testid="content-phone1" />
                <ContentField label="Phone 2" value={content.contact_phone2} onChange={(v) => setContent({ ...content, contact_phone2: v })} testid="content-phone2" />
              </div>
              <ContentField label="Contact Email" value={content.contact_email} onChange={(v) => setContent({ ...content, contact_email: v })} testid="content-email" />
            </div>
            <button data-testid="content-save" onClick={saveContent} disabled={savingContent}
              className="mt-8 bg-epic-gold text-epic-black font-bold uppercase tracking-widest text-xs px-8 py-3.5 hover:bg-epic-goldhover transition-colors disabled:opacity-60">
              {savingContent ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Event editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-epic-charcoal border border-white/10 w-full max-w-lg p-6 my-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-display text-2xl">{editing.id ? "Edit Event" : "Add Event"}</h3>
              <button data-testid="admin-event-close" onClick={() => setEditing(null)}><X className="text-white/60 hover:text-white" /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="event-form-title" placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} />
              <select data-testid="event-form-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-epic-charcoal">{c}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="event-form-date" type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className={inputCls} />
                <input data-testid="event-form-time" placeholder="8:00 PM" value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} className={inputCls} />
              </div>
              <textarea data-testid="event-form-desc" placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className={inputCls} />
              <input data-testid="event-form-image" placeholder="Image URL" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className={inputCls} />
              <input data-testid="event-form-ticket" placeholder="Ticket URL" value={editing.ticket_url} onChange={(e) => setEditing({ ...editing, ticket_url: e.target.value })} className={inputCls} />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="text-white/60 text-sm uppercase tracking-widest px-4">Cancel</button>
              <button data-testid="event-form-save" onClick={saveEvent} className="bg-epic-gold text-epic-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-epic-goldhover">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Booking detail modal */}
      {selected && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-epic-charcoal border border-white/10 w-full max-w-2xl p-6 my-auto max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-display text-2xl">{selected.full_name}</h3>
              <button data-testid="booking-detail-close" onClick={() => setSelected(null)}><X className="text-white/60 hover:text-white" /></button>
            </div>
            {selected.quote && (
              <div className="border border-epic-gold/30 p-4 mb-5">
                <p className="text-xs uppercase tracking-widest text-white/50">Auto Quote</p>
                <p className="font-display text-2xl text-epic-gold">${selected.quote.estimate_low?.toLocaleString()} – ${selected.quote.estimate_high?.toLocaleString()}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Email", selected.email], ["Phone", selected.phone], ["Organization", selected.organization],
                ["Contact via", selected.contact_method], ["Heard via", selected.referral_source],
                ["Event Type", selected.event_type], ["Visibility", selected.event_visibility],
                ["Date", selected.event_date], ["Alternate", selected.alternate_date],
                ["Time", `${selected.start_time || "?"} – ${selected.end_time || "?"}`], ["Arrival", selected.arrival_time],
                ["Guests", selected.guest_count], ["Rooms", (selected.preferred_rooms || []).join(", ")],
                ["Setup", selected.setup_style], ["Stage", selected.needs_stage], ["Kitchen", selected.needs_kitchen],
                ["Prep", selected.needs_prep], ["Alcohol", `${selected.alcohol_served} ${selected.alcohol_type}`],
                ["Production", (selected.production_needs || []).join(", ")], ["Live Ent.", `${selected.live_entertainment} ${selected.entertainment_type}`],
                ["Vendors", `${selected.vendors_present} ${selected.vendor_count}`], ["Budget", selected.budget],
                ["Tickets", selected.tickets_sold], ["Ticketing", selected.ticketing_support],
                ["Décor", selected.decor_brought], ["Early Access", selected.early_access], ["Breakdown", selected.next_day_breakdown],
              ].map(([k, v]) => (
                <div key={k}><span className="text-white/40 text-xs uppercase tracking-wider">{k}</span><p className="text-white">{v || "—"}</p></div>
              ))}
            </div>
            {selected.vision && (<div className="mt-4"><span className="text-white/40 text-xs uppercase tracking-wider">Vision</span><p className="text-white mt-1">{selected.vision}</p></div>)}
            <div className="flex justify-between items-center mt-6 pt-5 border-t border-white/10">
              <select value={selected.status} onChange={(e) => setStatus(selected.id, e.target.value)} className="bg-transparent border border-white/15 text-white text-sm px-3 py-2 focus:outline-none focus:border-epic-gold">
                {STATUSES.map((s) => <option key={s} value={s} className="bg-epic-charcoal">{s}</option>)}
              </select>
              <button data-testid="booking-detail-delete" onClick={() => deleteBooking(selected.id)} className="flex items-center gap-1 text-red-400 text-sm hover:text-red-300"><Trash2 size={15} /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
